import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Link,
    IconButton,
    InputAdornment,
    Alert,
    styled
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface ChangePasswordProps {
    onSave?: (passwords: {
        currentPassword: string;
        newPassword: string;
    }) => Promise<boolean>;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(0.75),
        backgroundColor: theme.palette.background.paper,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
        },
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(14px, -9px) scale(0.75)',
    },
    '& .MuiInputBase-input': {
        padding: '15px 14px',
    }
}));

const SaveButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: 500,
    backgroundColor: '#1e1e1e',
    '&:hover': {
        backgroundColor: '#000000',
    }
}));

const PasswordRequirement = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
}));

const ChangePassword: React.FC<ChangePasswordProps> = ({
    onSave = async (data) => {
        return true;
    }
}) => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [errors, setErrors] = useState<{
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }>({});

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }

        if (name === 'confirmPassword' || name === 'newPassword') {
            if (name === 'confirmPassword' && value !== formData.newPassword) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: 'Las contraseñas no coinciden'
                }));
            } else if (name === 'newPassword' && formData.confirmPassword && value !== formData.confirmPassword) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: 'Las contraseñas no coinciden'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: undefined
                }));
            }
        }
    };

    const validateForm = () => {
        const newErrors: typeof errors = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'La contraseña actual es requerida';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'La nueva contraseña es requerida';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Por favor confirma tu nueva contraseña';
        } else if (formData.confirmPassword !== formData.newPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setSuccess(false);

        try {
            const result = await onSave({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword
            });

            if (result) {
                setSuccess(true);
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            }
        } catch (error) {
            console.error('Error al cambiar la contraseña:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                p: 4,
                border: '1px solid #eaeaea',
                maxWidth: '100%'
            }}
        >
            <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="h5" component="h1" fontWeight={500} gutterBottom>
                    Cambiar contraseña
                </Typography>

                <Link
                    href="#"
                    underline="none"
                    color="primary"
                    sx={{
                        display: 'block',
                        mb: 3,
                        fontSize: '0.875rem'
                    }}
                >
                    Actualiza tu contraseña para mantener tu cuenta segura
                </Link>

                {success && (
                    <Alert
                        severity="success"
                        sx={{ mb: 3 }}
                        onClose={() => setSuccess(false)}
                    >
                        Tu contraseña ha sido actualizada exitosamente
                    </Alert>
                )}

                <Box sx={{ mt: 4 }}>
                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Contraseña actual
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={formData.currentPassword}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.currentPassword}
                        helperText={errors.currentPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        edge="end"
                                    >
                                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Nueva contraseña
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Box sx={{ mt: -2, mb: 2 }}>
                        <PasswordRequirement>
                            • Mínimo 8 caracteres
                        </PasswordRequirement>
                        <PasswordRequirement>
                            • Incluir al menos un número y un carácter especial
                        </PasswordRequirement>
                    </Box>

                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Confirmar nueva contraseña
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Box sx={{ mt: 5 }}>
                        <SaveButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            type="submit"
                            size="large"
                            disabled={submitting}
                        >
                            {submitting ? 'Guardando...' : 'Guardar cambios'}
                        </SaveButton>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export default ChangePassword;